// src/components/layout/NeumorphicCard.tsx
import { keyframes } from "@emotion/react";
import { Card, alpha, useTheme } from "@mui/material";

export interface NeumorphicCardProps {
  children: React.ReactNode;
  bgColor?: string;
  shadowColor?: string;
  sx?: object;
  onClick?: () => void;
  animate?: boolean; // Optional blinking animation
  fullHeight?: boolean;

  /**
   * Choose the visual style:
   * - "glass": Columbia-blue plasmorphic glass (radial gradient + blur + soft white border)
   * - "dark":  Dark plate with highlighted border (no fill or very low-opacity dark wash)
   */
  variant?: "glass" | "dark";

  /**
   * For "dark" variant only: set to true to make the background fully transparent
   * (i.e., no fill at all). If false, a very low-opacity dark wash is used.
   */
  darkTransparent?: boolean;

  /**
   * Opacity factor for the glass variant.
   *  - 0 → fully transparent
   *  - 1 → baseline as designed
   *  - >1 → denser glass
   */
  glassOpacityFactor?: number;

  /**
   * Opacity factor for the dark variant.
   *  - 0 → fully transparent
   *  - 1 → baseline as designed
   *  - >1 → denser dark plate
   */
  darkOpacityFactor?: number;
}

export const NeumorphicCard: React.FC<NeumorphicCardProps> = ({
  children,
  bgColor,
  shadowColor,
  sx = {},
  onClick,
  fullHeight = false,
  animate = false,
  variant = "dark",
  darkTransparent = false,
  glassOpacityFactor = 0.9,
  darkOpacityFactor = 1.0,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // ---------------------------
  // GLASS (plasmorphic) variant
  // ---------------------------
  // const glassBg = isDark
  //   ? `radial-gradient(circle at center,
  //       ${alpha(theme.palette.custom.secondary, 0.55 * glassOpacityFactor)} 0%,
  //       ${alpha(theme.palette.custom.secondary, 0.45 * glassOpacityFactor)} 70%,
  //       ${alpha(theme.palette.custom.secondary, 0.4 * glassOpacityFactor)} 100%
  //     )`
  //   : `radial-gradient(circle at center,
  //       ${alpha(theme.palette.custom.secondary, 0.45 * glassOpacityFactor)} 0%,
  //       ${alpha(theme.palette.custom.secondary, 0.35 * glassOpacityFactor)} 70%,
  //       ${alpha(
  //         theme.palette.custom.secondary,
  //         0.3 * glassOpacityFactor
  //       )} 100%)`;

  const glassBg = isDark
    ? `${alpha(theme.palette.secondary.main, 0.45 * glassOpacityFactor)}`
    : `${alpha(theme.palette.secondary.main, 0.45 * glassOpacityFactor)}`;

  const glassHoverBg = isDark
    ? `radial-gradient(circle at center,
        ${alpha(theme.palette.secondary.main, 0.65 * glassOpacityFactor)} 0%,
        ${alpha(theme.palette.secondary.main, 0.55 * glassOpacityFactor)} 70%,
        ${alpha(theme.palette.secondary.main, 0.5 * glassOpacityFactor)} 100%)`
    : `radial-gradient(circle at center,
        ${alpha(theme.palette.secondary.main, 0.55 * glassOpacityFactor)} 0%,
        ${alpha(theme.palette.secondary.main, 0.45 * glassOpacityFactor)} 70%,
        ${alpha(theme.palette.secondary.main, 0.4 * glassOpacityFactor)} 100%)`;

  const glassBorder = alpha(
    theme.palette.common.white,
    0.35 * glassOpacityFactor
  );
  const glassBorderHover = alpha(
    theme.palette.common.white,
    0.5 * glassOpacityFactor
  );

  // ---------------------------
  // DARK variant (highlighted border)
  // ---------------------------
  const darkBaseOpacity = (isDark ? 0.1 : 0.06) * darkOpacityFactor;
  const darkHoverOpacity = (isDark ? 0.14 : 0.08) * darkOpacityFactor;

  const darkBg = darkTransparent
    ? "transparent"
    : `linear-gradient(180deg,
        ${alpha(theme.palette.primary.dark, darkBaseOpacity)} 0%,
        ${alpha(theme.palette.primary.dark, darkBaseOpacity)} 100%)`;

  const darkHoverBg = darkTransparent
    ? "transparent"
    : `linear-gradient(180deg,
        ${alpha(theme.palette.primary.dark, darkHoverOpacity)} 0%,
        ${alpha(theme.palette.primary.dark, darkHoverOpacity)} 100%)`;

  const darkBorder = isDark
    ? alpha(theme.palette.secondary.main, 0.55 * darkOpacityFactor)
    : alpha(theme.palette.primary.dark, 0.35 * darkOpacityFactor);

  const darkBorderHover = isDark
    ? alpha(theme.palette.secondary.main, 0.75 * darkOpacityFactor)
    : alpha(theme.palette.primary.dark, 0.45 * darkOpacityFactor);

  // ---------------------------
  // Resolve current variant
  // ---------------------------
  const resolvedBg = bgColor || (variant === "glass" ? glassBg : darkBg);
  // const hoverBg = variant === "glass" ? glassHoverBg : darkHoverBg;
  const hoverBg = variant === "glass" ? glassBg : darkBg;
  const borderColor = variant === "glass" ? glassBorder : darkBorder;
  const hoverBorder = variant === "glass" ? glassBorderHover : darkBorderHover;

  // Shadows - reduced for cleaner look, respects border-radius
  const boxShadow = shadowColor
    ? `0 2px 8px ${shadowColor}15`
    : "0 2px 8px rgba(0, 0, 0, 0.08)";

  // Optional blinking animation
  const blinkAnimation = keyframes`
    0%   { opacity: 1;   border-width: 2px; border-color: ${theme.palette.divider}; }
    50%  { opacity: .35; border-width: 1px; border-color: ${theme.palette.divider}; }
    100% { opacity: 1;   border-width: 2px; border-color: ${theme.palette.divider}; }
  `;

  return (
    <Card
      onClick={onClick}
      sx={{
        position: "relative",
        background: resolvedBg, // ✅ gradients apply
        borderRadius: theme.shape.borderRadius / 4, // smaller radius, matches FloatingNeumorphicContainer
        boxShadow,
        border: `1px solid ${borderColor}`,
        m: theme.spacing(2.5),
        p: theme.spacing(2),
        cursor: onClick ? "pointer" : "default",
        height: fullHeight ? "100%" : "auto",
        display: "flex",
        flexDirection: "column",
        transition:
          "transform .18s ease, box-shadow .18s ease, background .18s ease, border-color .18s ease",
        backdropFilter: "blur(8px)",
        color: theme.custom?.contentText?.primary || theme.palette.text.primary,

        "&:hover": {
          // Keep background the same on hover - only border changes
          borderColor: hoverBorder,
        },

        "&:active": {
          boxShadow: `inset 0 1px 3px ${alpha(
            theme.palette.primary.dark,
            0.1
          )}`,
        },

        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 3px ${theme.palette.secondary.main}66`,
        },

        ...(animate && {
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: theme.shape.borderRadius,
            pointerEvents: "none",
            borderStyle: "solid",
            animation: `${blinkAnimation} 4s ease-in-out infinite`,
          },
        }),

        ...sx,
      }}
    >
      {children}
    </Card>
  );
};
